import { ApiError } from '@mechhive/api';
import { isStringEmpty } from '@mechhive/helpers';
import { Image, ImageFit, ImageFormat, ImageLoading } from '@mechhive/react';
import { createSeoAlternateLinks, createSeoCanonical, createSeoMeta, notFound } from '@mechhive/remix';
import * as Sentry from '@sentry/react';
import { NavLink } from 'react-router';
import { createRewarbleApi } from '~/api/rewarble.api.server';
import { createStoryblokApi } from '~/api/storyblok.api.server';
import type { StoryblokDoc } from '~/api/types/storyblok/blocks/StoryblokDoc';
import type { StoryblokPageBrand } from '~/api/types/storyblok/pages/StoryblokPageBrand';
import type { StoryblokPageIndex } from '~/api/types/storyblok/pages/StoryblokPageIndex';
import { Button } from '~/components/Button';
import { BrandCard } from '~/components/content/BrandCard';
import { FAQ, FAQItem } from '~/components/content/FAQ';
import { FeeCalculator } from '~/components/content/FeeCalculator';
import { InfoBlock } from '~/components/content/InfoBlock';
import { MarqueeScroller, MarqueeScrollerDirection } from '~/components/content/MarqueeScroller';
import { StoryblokRichText } from '~/components/storyblok/StoryblokRichText';
import { TrustpilotBanner } from '~/components/trustpilot/TrustpilotBanner';
import { SubTitle } from '~/components/typograph/SubTitle';
import { Title } from '~/components/typograph/Title';
import { IconName } from '~/components/ui/Icon';
import { useLanguageServer } from '~/hooks/useLanguage.server';
import { useLocalePath } from '~/hooks/useLocalePath';
import { usePageColor } from '~/hooks/usePageColor';
import { useRegion } from '~/hooks/useRegion';
import { useRegionServer } from '~/hooks/useRegion.server';
import { useTranslate } from '~/hooks/useTranslate';
import { useUserAccount } from '~/hooks/useUserAccount';
import { META_LANGUAGES } from '~/locale';
import { resellers } from '~/resellers';
import { createMetaUrl } from '~/utils/createMetaUrl';
import { createSkineUrl } from '~/utils/createSkineUrl';
import { getMetaBaseUrl } from '~/utils/getMetaBaseUrl';
import type { Route } from './+types/($lang).brands.$brandSlug._index';

export const loader = async ( { request, params, context } : Route.LoaderArgs ) => {
  const rewarbleApi = createRewarbleApi( request );
  const storyblokApi = createStoryblokApi();
  const language = useLanguageServer( params );
  const region = await useRegionServer( request );
  
  try {
    const regionResponse = await rewarbleApi.web.getRegionSafe( region );
    const brand = regionResponse.data[0].brands.find( b => b.slug === params.brandSlug );
    if ( 
      brand == null ||
      brand.isCryptoBrand === true
    ) {
      throw notFound();
    }
  
    const [ storyIndexResponse, storyBrandResponse, fxResponse ] = await Promise.all( [
      storyblokApi.getStory<StoryblokPageIndex>( 'pages/index', language ),
      storyblokApi.getStory<StoryblokPageBrand>( `brands/${params.brandSlug}`, language ),
      rewarbleApi.web.getFx(),
    ] );

    const pricing = regionResponse.data[0].pricing.find( p => p.brand === brand.slug );
    return {
      benefits: ( storyBrandResponse.data.story.content.benefits?.length ?? 0 ) > 0 ? storyBrandResponse.data.story.content.benefits : storyIndexResponse.data.story.content.infoBlocks,
      story: storyBrandResponse.data.story.content,
      brandSlug: params.brandSlug,
      brand,
      relatedBrands: regionResponse.data[0].brands.filter( b => brand.relatedBrands.includes( b.slug ) ),
      resellers: resellers,
      pricing,
      fx: fxResponse.data,
      regionCode: regionResponse.data[0].regionCode
    }
  } catch ( ex ) {
    if ( ex instanceof ApiError ) {
      throw ex.toResponse();
    }
    
    if ( ex instanceof Response ) {
      throw ex;
    }

    Sentry.captureException( ex );
  }

  throw notFound();
}

export const meta : Route.MetaFunction = ( { data, params, matches } ) => {
  const path = `/brands/${data?.brand.slug}`;
  const baseUrl = getMetaBaseUrl( matches );
  const url = createMetaUrl( {
    baseUrl,
    path,
    lang: params.lang
  } );

  return [
    ...createSeoMeta( {
      title: data?.story.metaFields.title,
      description: data?.story.metaFields.description,
      og: {
        title: data?.story.metaFields.title,
        description: data?.story.metaFields.description,
        url,
        image: 'https://cdn.rewarble.com/images/static/feature-rewarble-image.jpg'
      }
    } ),
    createSeoCanonical( {
      url
    } ),
    ...createSeoAlternateLinks( {
      baseUrl,
      defaultLocale: 'en',
      path,
      locales: META_LANGUAGES.map( m => ( {
        locale: m.lang,
        pathPrefix: m.pathPrefix ?? ''
      } ) )
    } )
  ]
}

export default function BrandPage( { loaderData }: Route.ComponentProps ) {
  const localePath = useLocalePath();
  const t = useTranslate();
  const userAccount = useUserAccount();
  const userRegion = useRegion();
  const skineUrl = createSkineUrl( loaderData.brand.snidderBrandSlug ? `/${loaderData.brand.snidderBrandSlug}` : undefined, { utmMedium: 'link' } )

  const pricing = loaderData.pricing?.options.map( option => ( {
    currency: option.currency,
    feeCurrency: option.feeCurrency,
    feeFixed: option.feeFixed,
    feeVariable: option.feeVariable,
    minAmount: option.minAmount,
    maxAmount: option.maxAmount,
  } ) );
  
  usePageColor( loaderData.brand.backgroundColor, [loaderData.brand] );
  
  return (
    <div className={ 'container mx-auto px-4 flex flex-col gap-24 lg:gap-[100px] items-center' }>
      <div className={ 'flex flex-col items-center justify-center gap-[60px] w-full' }>
        <div className={ 'flex flex-col gap-2.5 items-center' }>
          <Title
            gradient={ false }
            headingLevel={ 1 }>
            { loaderData.story.title }
          </Title>
          <SubTitle className={ 'text-center' }>
            { loaderData.story.subTitle }
          </SubTitle>
        </div>
        <div className='w-full flex flex-col justify-center items-center gap-[80px]'>
          {  ( ( !loaderData.brand.unavailableRegions.includes( userRegion ) ) && 
              ( loaderData.brand.status !== 'suspended' && 
              !['giftcard', 'game-card', 'payment-card'].includes( loaderData.brand.subCategory ) ) )  &&
                <div className='w-full grid grid-cols-1 md:grid-cols-3 order-3 md:order-1 gap-[40px] md:gap-[60px] md:w-full'>
                  { loaderData.benefits?.map( ( benefit, index ) => {
                    return (
                      <InfoBlock 
                        key={ benefit._uid }
                        title={ benefit.title }
                        content={ benefit.content }
                        iconName={ benefit.iconName }
                      />
                    )
                  } )}
                </div>
          }
          <div className='md:w-[640px] flex flex-col gap-10 items-center w-full order-1 md:order-2'>  
            <div className='px-[16%] w-full lg:px-0 lg:w-[240px]'>
              <BrandCard
                brandSlug={ loaderData.brandSlug }
                hoverInteraction={ false }
                width={ 240 } 
                sizes={ [
                  { maxWidth: 768, width: 412 },
                  { maxWidth: 1024, width: 367 },
                  { width: 240 }
                ] }
              />
            </div>  
            {  ( ( !loaderData.brand.unavailableRegions.includes( userRegion ) ) && ( loaderData.brand.status !== 'suspended' ) )  &&
              <div className='w-full flex flex-col sm:flex-row gap-8'>
                <Button 
                  to={ skineUrl }
                  className='flex-1 whitespace-nowrap'
                  iconName={ IconName.ArrowUpRightFromSquare }
                  variant={ 'secondary' }
                >
                  { !isStringEmpty( loaderData.story.buyButton ) ? loaderData.story.buyButton : t( 'BRAND_PAGE_BUY_BUTTON' ) }
                </Button>
                <Button
                  className='flex-1 whitespace-nowrap'
                  to={ localePath( userAccount ? `/store/${loaderData.brand.slug}` : '/redeem' ) }
                  variant={ 'secondary' }
                >
                  { !isStringEmpty( loaderData.story.redeemButton ) ? loaderData.story.redeemButton : t( 'BRAND_PAGE_REDEEM_BUTTON' ) }
                </Button>
              </div>
            }
          </div>
          { ( loaderData.brand.unavailableRegions.includes( userRegion ) || loaderData.brand.status === 'suspended' ) &&
            <div className="flex flex-col items-center gap-5 text-center order-2 md:order-3 md:max-w-[725px]">
              <div className="text-[#e16a45] text-4xl md:text-5xl font-medium shadow">
                {loaderData.brand.unavailableRegions.includes( userRegion ) ? (
                  <div>
                    {!isStringEmpty( loaderData.story.productUnavailableInRegionTitle ) ? loaderData.story.productUnavailableInRegionTitle : t( 'BRAND_REGION_UNAVAILABLE_FALLBACK_TITLE' )}
                  </div>
                ) : (
                  <div>
                    {!isStringEmpty( loaderData.story.productUnavailableTitle ) ? loaderData.story.productUnavailableTitle : t( 'BRAND_UNAVAILABLE_FALLBACK_TITLE' )}
                  </div>
                )}
              </div>
              { ( loaderData.brand.unavailableRegions.includes( userRegion ) && loaderData.story.productUnavailableInRegionTitle == null && isStringEmpty( loaderData.story.productUnavailableInRegionTitle ) ) &&
                <p>{t( 'BRAND_REGION_UNAVAILABLE_FALLBACK_MESSAGE' )}</p>
              }

              { (
                !loaderData.brand.unavailableRegions.includes( userRegion ) &&
                loaderData.brand.status === 'suspended' &&
                loaderData.story.productUnavailableMessage
              ) &&
                <StoryblokRichText>
                  {loaderData.story.productUnavailableMessage as StoryblokDoc}
                </StoryblokRichText>
              }
              { (
                !loaderData.brand.unavailableRegions.includes( userRegion ) &&
                loaderData.brand.status === 'suspended' &&
                loaderData.story.productUnavailableMessage == null
              ) &&
                <p>{t( 'BRAND_UNAVAILABLE_FALLBACK_MESSAGE' )}</p>
              }
            </div>
          }
        </div> 
        { ( loaderData.brand.status === 'active' && !loaderData.brand.unavailableRegions.includes( userRegion ) ) &&
          <TrustpilotBanner />
        }
        <div className=' md:w-[640px] flex flex-col'>
          <StoryblokRichText>
            { loaderData.story.content as StoryblokDoc }
          </StoryblokRichText>
        </div>
      </div>
      { pricing &&
        <div className='flex flex-col gap-10 items-center w-full'>
          <div className='flex flex-col w-2/3 gap-[30px]'>
            <Title headingLevel={ 2 }>{t( 'BRAND_PAGE_FEE_CALCULATOR_TITLE' )}</Title>
            <SubTitle>{t( 'BRAND_PAGE_FEE_CALCULATOR_SUBTITLE' )}</SubTitle>
          </div>
          <div className='flex flex-col items-center gap-[30px] w-full'>
            <h3 className='text-xl font-semibold'>
              {t( 'BRAND_PAGE_FEE_CALCULATOR_TITLE_CONVERTER' )}
            </h3>
            <div className='w-full'>           
              <FeeCalculator
                products={ pricing || [] }
                fxRate={ loaderData.fx }/>
            </div>
          </div>
        </div>}
      <div className='w-full flex flex-col items-center gap-16 overflow-clip'>
        <div className='flex flex-col gap-3'>
          <Title headingLevel={ 2 }>{t( 'TRUSTPARTNERS_TITLE' )}</Title>
          <SubTitle>{t( 'TRUSTPARTNERS_SUBTITLE' )}</SubTitle>
        </div>
        <div className={ 'flex flex-col gap-14 w-full relative justify-center items-center' }>
          <div className={ 'w-24 absolute inset-y-0 bg-gradient-to-r from-[#05051E] via-[#05051E]/20 to-[#05051E]/0 z-10 left-0' }></div>
          <div className={ 'w-24 absolute inset-y-0 bg-gradient-to-l from-[#05051E] via-[#05051E]/20 to-[#05051E]/0 z-10 right-0' }></div>
          <div className='flex flex-col gap-6'>
            <MarqueeScroller
              duration={ 120 }
              contentClassName={ 'gap-[50px]' }>
              <div className={ 'flex flex-row items-center gap-[50px]' }>
                {
                  loaderData.resellers.map( ( reseller, index ) => {
                    return (
                      <a
                        key={ index }
                        href={ reseller.image.endsWith( 'skine.png' ) ? skineUrl : reseller.link }
                        target={ '_blank' }
                        rel={ reseller.follow === true ? 'noreferrer' : 'nofollow noreferrer' }>
                        <Image
                          provider='images'
                          src={ reseller.image }
                          formats={ [
                            ImageFormat.WebP,
                            ImageFormat.PNG
                          ] }
                          width={ 120 }
                          height={ 30 }
                          sizes={ [{
                            width: 225,
                            height: 30
                          }] }
                          fit={ ImageFit.Outside }
                          loading={ ImageLoading.Lazy }
                          dprVariants={ [1,3] }
                        />
                      </a>
                    )
                  } )
                }
              </div>
            </MarqueeScroller>
            <MarqueeScroller
              duration={ 120 }
              direction={ MarqueeScrollerDirection.Left }
              contentClassName={ 'gap-[50px]' }>
              <div className={ 'flex flex-row items-center gap-[50px]' }>
                {
                  loaderData.resellers.map( ( reseller, index ) => {
                    return (
                      <a
                        key={ index }
                        href={ reseller.image.endsWith( 'skine.png' ) ? skineUrl : reseller.link }
                        target={ '_blank' }
                        rel={ reseller.follow === true ? 'noreferrer' : 'nofollow noreferrer' }>
                        <Image
                          provider='images'
                          src={ reseller.image }
                          formats={ [
                            ImageFormat.WebP,
                            ImageFormat.PNG
                          ] }
                          width={ 120 }
                          height={ 30 }
                          sizes={ [{
                            width: 225,
                            height: 30
                          }] }
                          fit={ ImageFit.Outside }
                          loading={ ImageLoading.Lazy }
                          dprVariants={ [1,3] }
                        />
                      </a>
                    )
                  } )
                }
              </div>
            </MarqueeScroller>
          </div>
          <Button
            variant={ 'secondary' }
            to={ localePath( '/distributors' ) }>
            {t( 'TRUSTPARTNERS_SEE_ALL' )}
          </Button>
        </div>
      </div>
      <div className='w-full flex flex-col justify-center items-center gap-10'>
        <div className='font-semibold text-xl'>{t( 'BRANDS_RELATED_PRODUCTS' )}</div>
        <div className={ 'w-full' }>
          <div className={ 'grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-x-4 gap-y-8 xl:gap-12' }>
            { loaderData.relatedBrands.map( ( brand ) => {
              return (
                <NavLink
                  key={ brand.slug }
                  to={ localePath( `/brands/${brand.slug}` ) }
                  className={ 'w-full' }
                >
                  <BrandCard 
                    brandSlug={ brand.slug }
                    title={ brand.name }
                    sizes={ [
                      { maxWidth: 768, width: 296 },
                      { maxWidth: 1024, width: 234 },
                      { width: 168 }
                    ] }
                    loading={ ImageLoading.Lazy }
                  />
                </NavLink>
              )
            } )}
          </div>
        </div>
      </div>
      <FAQ className={ 'w-full max-w-[656px]' }>
        { loaderData.story.faqItems.map( ( faqItem ) => {
          return (
            <FAQItem 
              key={ faqItem._uid }
              title={ faqItem.title }
              content={ faqItem.content as StoryblokDoc }
            />
          )
        } )}
      </FAQ>
    </div>
  )
}